/**
 * Resuelve los datos de diseño de una tarjeta. `GET /checkout/payment/card` ya trae banco,
 * medio de pago y categoría, así que se usan directamente; si faltan, la marca se infiere por
 * prefijo de BIN. Detrás de una interfaz por si el diseño pasa a resolverlo un servicio por BIN.
 */
import type { SavedCard } from '../domain/models';

export interface CardDesign {
  issuerName: string;
  network: string;
  networkVariant: string;
  gradientColors: [string, string];
  /** Solo la tarjeta de crédito Carrefour (Mastercard): no incluye Cuenta Digital ni prepaga. */
  issuer?: 'carrefour';
}

export interface CardDesignResolver {
  resolve(card: SavedCard): CardDesign;
}

const GENERIC_NETWORK = 'Tarjeta';
const GENERIC_GRADIENT: [string, string] = ['#005DAA', '#003D73'];

interface NetworkDefinition {
  name: string;
  gradient: [string, string];
  matchesName: (paymentMethod: string) => boolean;
  matchesBin: (bin: string) => boolean;
}

const NETWORKS: readonly NetworkDefinition[] = [
  {
    name: 'Visa',
    gradient: ['#1A3FA0', '#0B1F5C'],
    matchesName: (value) => /visa/i.test(value),
    matchesBin: (bin) => bin.startsWith('4'),
  },
  {
    name: 'Mastercard',
    gradient: ['#DD565B', '#BF2344'],
    matchesName: (value) => /master/i.test(value),
    matchesBin: (bin) => {
      const two = Number(bin.slice(0, 2));
      const four = Number(bin.slice(0, 4));
      return (two >= 51 && two <= 55) || (four >= 2221 && four <= 2720);
    },
  },
  {
    name: 'American Express',
    gradient: ['#2E8B57', '#1B5E3A'],
    matchesName: (value) => /american|amex/i.test(value),
    matchesBin: (bin) => bin.startsWith('34') || bin.startsWith('37'),
  },
];

/**
 * BIN de la tarjeta de crédito Mi Carrefour (Mastercard): 538156 Internacional, 520225 Gold,
 * 539958 Platinum y 527061 Signature-Black (`bank:mi_carrefour` en V10__seed_card_theme.sql del
 * payment-service). Cuenta Digital (223364, débito) y prepaga quedan afuera a propósito.
 */
const CARREFOUR_CREDIT_BINS: readonly string[] = ['538156', '520225', '539958', '527061'];

const isCarrefourCreditCard = (card: SavedCard, variant: string): boolean => {
  if (variant !== 'Crédito') return false;
  const bank = card.bank ?? '';
  if (/digital|prepag/i.test(bank)) return false;
  return CARREFOUR_CREDIT_BINS.includes(card.bin.slice(0, 6)) || /carrefour/i.test(bank);
};

export const createCardDesignResolver = (): CardDesignResolver => ({
  resolve: (card) => {
    const network =
      NETWORKS.find((candidate) => candidate.matchesName(card.paymentMethod ?? '')) ??
      NETWORKS.find((candidate) => candidate.matchesBin(card.bin));
    const networkName = network?.name ?? GENERIC_NETWORK;

    const networkVariant = /deb/i.test(card.paymentMethod ?? '') ? 'Débito' : 'Crédito';

    return {
      issuerName: card.bank || networkName,
      network: networkName,
      networkVariant,
      gradientColors: network?.gradient ?? GENERIC_GRADIENT,
      ...(isCarrefourCreditCard(card, networkVariant) ? { issuer: 'carrefour' as const } : {}),
    };
  },
});
